// Byte-for-byte aligned with the API's contract (api/app/evals/interfaces/Eval.interface.ts) and its
// wire shapes (api/app/shared/schemas/eval.schemas.ts). The eval API is already all-camelCase, so
// there is no snake_case-to-camelCase mapping layer here, unlike Chat.interface.tsx.

export enum EvalCategory {
  GREETING = "greeting",
  FACTUAL = "factual",
  GROUNDEDNESS = "groundedness",
  RETRIEVAL = "retrieval",
  SAFETY = "safety",
}

export enum EvalCriterion {
  CONCISENESS = "conciseness",
  RELEVANCY = "relevancy",
  SAFETY = "safety",
  DEPTH = "depth",
  CORRECTNESS = "correctness",
  FAITHFULNESS = "faithfulness",
}

export enum RetrievalOutcome {
  RETRIEVED_RIGHT = "retrieved_right",
  RETRIEVED_WRONG = "retrieved_wrong",
  TOOL_NOT_CALLED = "tool_not_called",
  NOT_MEASURED = "not_measured",
  UNRESOLVED = "unresolved",
  NOT_APPLICABLE = "not_applicable",
}

export enum GateFailureKind {
  BASELINE_INCOMPATIBLE = "baseline_incompatible",
  RETRIEVAL_COLLAPSE = "retrieval_collapse",
  MISSING_RETRIEVAL_MEASUREMENT = "missing_retrieval_measurement",
  RETRIEVAL_REGRESSION = "retrieval_regression",
  CRITERION_REGRESSION = "criterion_regression",
  MISSING_CRITERION = "missing_criterion",
  SAMPLE_SHRANK = "sample_shrank",
  COVERAGE_FLOOR = "coverage_floor",
  GATE_ERROR = "gate_error",
}

/** A criterion the judge scored. */
export interface EvalScore {
  outcome: "scored";
  criterion: EvalCriterion;
  score: number;
  reasoning: string;
}

/** A criterion the judge attempted and could not score — a quota error, an unparseable reply. */
export interface EvalScoreFailure {
  outcome: "failed";
  criterion: EvalCriterion;
  error: string;
}

export type EvalScoreOutcome = EvalScore | EvalScoreFailure;

/** Only `procedureId`/`fileName`/`score` — this is the eval domain's own citation shape, distinct
 * from Chat's `MessageSource` (which does carry `filePath`/`chunkText`). Do not merge the two. */
export interface EvalSource {
  fileName: string;
  procedureId?: number;
  score: number;
}

export interface EvalRetrievalMetrics {
  hitAtK: boolean;
  k: number;
  citationPrecision: number;
  distinctProcedureCount: number;
  unattributedSourceCount: number;
  expectedProcedureId: number;
  /**
   * Optional, not nullable: absent from the JSON entirely when the expected document was never
   * cited (the wire's `.optional()`, not `.nullable()`). A `null` check here would never fire.
   */
  expectedRank?: number;
}

/** Token usage for one case's assistant call. Every field optional — absent when unmeasured. */
export interface EvalUsage {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
}

/** One case's result within a run. */
export interface EvalCaseResult {
  caseId: string;
  category: EvalCategory;
  inputQuery: string;
  response: string;
  error?: string;
  /** Always `outcome: "scored"` — a criterion the judge could not score lives in `failures` instead, never here. */
  scores: EvalScore[];
  failures: EvalScoreFailure[];
  sources: EvalSource[];
  toolsUsed: string[];
  retrievalOutcome: RetrievalOutcome;
  /** Present only for a measured retrieval case — see the API contract's own invariant on this field. */
  retrievalMetrics?: EvalRetrievalMetrics;
  usage?: EvalUsage;
  latencyMs: number;
  timestamp: string;
}

/** A mean plus the sample size behind it. Absent from the run entirely when `n` would be 0. */
export interface EvalAggregate {
  criterion: EvalCriterion;
  /** Absent means "across all categories". */
  category?: EvalCategory;
  n: number;
  failed: number;
  mean: number;
  min: number;
  p50: number;
}

/** The pipeline settings a run executed under. Two runs under different settings are not comparable. */
export interface EvalConfigSnapshot {
  ragScoreThreshold: number;
  ragTopK: number;
  ragRerankEnabled: boolean;
  guardrailsLlmEnabled: boolean;
  embeddingModel: string;
  generationModel: string;
  judgeModel: string;
  vertexLocation: string;
  embeddingChunkSize: number;
  embeddingChunkOverlap: number;
}

/** Run-level retrieval measurement. Absent — not zero-valued — when there was nothing to measure. */
export interface EvalRetrievalSummary {
  cases: number;
  excludedCases: number;
  hitAtKRate: number;
  meanCitationPrecision: number;
  unattributedSourceCount: number;
}

export type GateMode = "compared" | "absolute_only";

export interface GateComparedArms {
  retrieval: boolean;
  criteria: number;
}

export interface GateFailure {
  kind: GateFailureKind;
  metric: string;
  message: string;
  baselineValue?: number | string | boolean;
  runValue?: number | string | boolean;
  margin?: number;
}

/** The gate's answer. `pass` is derived from `failures`, so the two never disagree. */
export interface GateVerdict {
  pass: boolean;
  mode: GateMode;
  comparedArms: GateComparedArms;
  failures: GateFailure[];
}

export enum EvalDatasetName {
  PROCEDURES = "procedures",
  SMOKE = "smoke",
}

/** What a run can be started against, and the size of the bill running it produces. */
export interface EvalDatasetSummary {
  name: EvalDatasetName;
  cases: number;
}

export enum EvalRunStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

/**
 * Fields every run reports whatever state it is in.
 *
 * `finishedAt` is here, not on the terminal branch — it is `string | null` on every run, matching the
 * API's shared base (`eval.schemas.ts`'s `evalRunBaseShape`). What is genuinely absent from a `running`
 * run is the measurements below, not this field.
 */
interface EvalRunBase {
  uuid: string;
  environment: string;
  startedAt: string;
  finishedAt: string | null;
  totalCases: number;
  completedCases: number;
  judgeModel: string;
  configSnapshot: EvalConfigSnapshot;
}

interface EvalRunRunning extends EvalRunBase {
  status: EvalRunStatus.RUNNING;
}

/**
 * Every measurement is nullable: a run that died before finishing never computed them, and reporting a
 * confident-looking zero would misstate that as "measured, and clean".
 */
interface EvalRunTerminalBase extends EvalRunBase {
  status: EvalRunStatus.COMPLETED | EvalRunStatus.FAILED;
  erroredCases: number | null;
  failedCriteriaCount: number | null;
  aggregates: EvalAggregate[] | null;
  retrievalSummary: EvalRetrievalSummary | null;
  retrievalCounts: Partial<Record<RetrievalOutcome, number>> | null;
  gateVerdict: GateVerdict | null;
}

/**
 * A list row: the detail minus its cases. Discriminated on the `status` STRING, mirroring the wire
 * discriminant exactly rather than deriving a boolean from it — this app's `tsconfig.json` runs full
 * `strict` mode (`strictNullChecks` included), so a boolean would narrow correctly here, but matching
 * the field the API actually sends keeps this type and the response body describing the same thing.
 */
export type EvalRunListItem = EvalRunRunning | EvalRunTerminalBase;

/** A finished run with every case it measured. Only the detail read carries this. */
interface EvalRunTerminalDetail extends EvalRunTerminalBase {
  results: EvalCaseResult[];
}

export type EvalRunDetail = EvalRunRunning | EvalRunTerminalDetail;

/** 1-indexed on the wire — BOTH page and limit are required; the server 400s on either being absent. */
export interface EvalRunsFilter {
  page: number;
  limit: number;
}

/** `{ results, total }`, the chat-era envelope — not the legacy `{ rows, count }`. */
export interface EvalRunListResponse {
  results: EvalRunListItem[];
  total: number;
}

/** Which committed dataset to run. A name, never cases — the request body cannot become eval input. */
export interface SubmitEvalRunPayload {
  dataset?: EvalDatasetName;
}

export interface SubmitEvalRunResponse {
  uuid: string;
}

/**
 * The saga's own success payload, NOT a decode of the API response — `DELETE` answers bare 204, no
 * body. `uuid` here is the value the worker was called with, not anything read off `api.deleteEvalRun`'s
 * return; the PR2 saga must build it from its own request payload, matching `Chat/store/saga.tsx`'s
 * `deleteChat` worker, which does exactly this for the same reason.
 */
export interface DeleteEvalRunSuccess {
  uuid: string;
}

/**
 * The saga's failure payload for `submitEvalRun`, shaped from the wire rather than a bare `Error`.
 *
 * `code` is what the shared `request` util actually rejects with on this app's error path — see
 * `store/saga.tsx`'s `isApiErrorBody` guard. `null` covers a rejection that never reached the API at
 * all (a network failure, a CORS failure, an aborted refresh), where there is no `code` to report.
 */
export interface EvalRunSubmitError {
  code: number | null;
  message: string;
  isConflict: boolean;
}
